import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { User, Client } from '../../types';
import { fullName } from '../../utils/name';
import UserForm from './UserForm';
import Footer from '../dashboard/Footer';
import './UserDetail.css';

const UserDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { user, isAdmin, isUserAdmin } = useAuth();
  const [userDetail, setUserDetail] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [togglingStatus, setTogglingStatus] = useState(false);
  const [showEditForm, setShowEditForm] = useState(false);
  const [clients, setClients] = useState<Client[]>([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  useEffect(() => {
    if (!isAdmin() && !isUserAdmin()) {
      setError('Unauthorized');
      setLoading(false);
      return;
    }
    fetchUser();
    fetchClients();
  }, [id]);

  const fetchClients = async () => {
    try {
      setClientsLoading(true);
      const response = await api.get<Client[]>('/clients');
      // Filter only active clients
      const activeClients = response.data.filter(client => {
        const statusValue = client.status as boolean | number | undefined;
        return statusValue !== false && statusValue !== 0;
      });
      setClients(activeClients);
    } catch (err: any) {
      console.error('Failed to fetch clients:', err);
    } finally {
      setClientsLoading(false);
    }
  };

  const fetchUser = async () => {
    try {
      setLoading(true);
      const response = await api.get<User>(`/users/${id}`);
      setUserDetail(response.data);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!userDetail) return;
    
    try {
      setDeleting(true);
      await api.delete(`/users/${userDetail.id}`);
      // Navigate back to users list after successful deletion
      navigate(isUserAdmin() ? '/dashboard/users' : '/dashboard?tab=users');
    } catch (err: any) {
      console.error('Failed to delete user:', err);
      setError(err.response?.data?.message || 'Failed to delete user');
      setShowDeleteConfirm(false);
    } finally {
      setDeleting(false);
    }
  };

  const toggleUserStatus = async () => {
    if (!userDetail) return;
    
    try {
      setTogglingStatus(true);
      // Handle both boolean (true/false) and integer (1/0) status values
      const statusValue = userDetail.status as boolean | number | undefined;
      const currentStatus = statusValue !== false && statusValue !== 0;
      
      const response = await api.patch<User>(`/users/${userDetail.id}/status`, {
        status: !currentStatus,
      });
      
      // Update local state immediately with the response
      setUserDetail(response.data);
    } catch (err: any) {
      console.error('Failed to toggle user status:', err);
      setError(err.response?.data?.message || 'Failed to toggle user status');
    } finally {
      setTogglingStatus(false);
    }
  };

  if (loading) {
    return (
      <div className="detail-container">
        <div className="loading">Loading user...</div>
      </div>
    );
  }

  if (error || !userDetail) {
    return (
      <div className="detail-container">
        <div className="error-message">{error || 'User not found'}</div>
        <button onClick={() => navigate(isUserAdmin() ? '/dashboard/users' : '/dashboard?tab=users')} className="btn btn-primary">
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <>
    <div className="detail-container">
      <div className="detail-header">
        <button onClick={() => navigate(isUserAdmin() ? '/dashboard/users' : '/dashboard?tab=users')} className="back-button">
          ← Back to Users
        </button>
        <h1>User Details</h1>
      </div>

      <div className="user-detail-card">
        <div className="user-header-section">
          <div className="user-header-content">
            <div>
              <h2>{fullName(userDetail)}</h2>
              <span className={`role-badge ${userDetail.role}`}>
                {userDetail.role === 'user_admin' ? 'Admin User' : userDetail.role}
              </span>
            </div>
            <div style={{ display: 'flex', gap: '10px' }}>
              <button
                type="button"
                onClick={() => setShowEditForm(true)}
                className="edit-button"
                title="Edit user"
              >
                ✏️ Edit
              </button>
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(true)}
                className="delete-button"
                title="Delete user"
              >
                🗑️ Delete
              </button>
            </div>
          </div>
        </div>

        <div className="user-info-section">
          <div className="info-item">
            <span className="info-label">Status:</span>
            <span className="info-value">
              {(() => {
                const statusValue = userDetail.status as boolean | number | undefined;
                const isActive = statusValue !== false && statusValue !== 0;
                return (
                  <button
                    type="button"
                    onClick={toggleUserStatus}
                    className={`status-toggle ${isActive ? 'active' : 'inactive'}`}
                    disabled={togglingStatus}
                    title={isActive ? 'Click to deactivate' : 'Click to activate'}
                  >
                    {togglingStatus ? '...' : (isActive ? '✓ Active' : '✗ Inactive')}
                  </button>
                );
              })()}
            </span>
          </div>
          <div className="info-item">
            <span className="info-label">ID:</span>
            <span className="info-value">{userDetail.id}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Email:</span>
            <span className="info-value">{userDetail.email}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Role:</span>
            <span className="info-value">
              <span className={`role-badge ${userDetail.role}`}>
                {userDetail.role === 'user_admin' ? 'Admin User' : userDetail.role}
              </span>
            </span>
          </div>
        </div>

        {userDetail.clients && userDetail.clients.length > 0 && (
          <div className="clients-section">
            <h3>Associated Companies</h3>
            <div className="clients-list">
              {userDetail.clients.map((client) => (
                <div 
                  key={client.id} 
                  className="client-item"
                  onClick={() => navigate(`/dashboard/clients/${client.id}`)}
                >
                  <span className="client-name">{client.client_name}</span>
                  <span className="client-id">ID: {client.id}</span>
                </div>
              ))}
            </div>
          </div>
        )}

        {(!userDetail.clients || userDetail.clients.length === 0) && (
          <div className="no-clients">
            <p>This user is not associated with any companies.</p>
          </div>
        )}
      </div>

      {showEditForm && (
        <div className="modal-overlay" onClick={() => setShowEditForm(false)}>
          <div className="modal-content edit-modal-content" onClick={(e) => e.stopPropagation()}>
            <UserForm
              clients={clients}
              clientsLoading={clientsLoading}
              editUser={userDetail}
              onSuccess={() => {
                setShowEditForm(false);
                fetchUser();
              }}
              onCancel={() => setShowEditForm(false)}
              allowAdminRole={!isUserAdmin()}
            />
          </div>
        </div>
      )}

      {showDeleteConfirm && (
        <div className="modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3>Delete User</h3>
            <p>Are you sure you want to delete <strong>{fullName(userDetail)}</strong>?</p>
            <p className="warning-text">This action cannot be undone. The user will be soft-deleted.</p>
            <div className="modal-actions">
              <button
                type="button"
                onClick={() => setShowDeleteConfirm(false)}
                className="btn btn-secondary"
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDelete}
                className="btn btn-danger"
                disabled={deleting}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
    <Footer />
    </>
  );
};

export default UserDetail;
